import { useMemo, useState } from "react";
import { IoClose } from "react-icons/io5";
import { ChevronDown } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Cookies from "js-cookie";
import { toast } from "sonner";
import { AxiosError } from "axios";

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu/dropdown-menu";
import { cn } from "@/lib/utils";
import {
  addAttendanceCommmentByNewWay,
  changeAttendanceStatusByAdmin,
} from "@/config/mutation/employee";
import { BiComment } from "react-icons/bi";
import { Input } from "@/components/ui/input/input";
import { Button } from "@/components/ui/button/button";
import { format } from "date-fns";
import { useParams } from "react-router-dom";
import {
  getAttendanceCommentsByDateAsAdmin,
  getCurrentAdmin,
} from "@/config/queries/admin/admin-queries";
import { AttendanceChangeModal } from "@/components/attendance-change-modal/attendance-change-modal";

export type Attendance = {
  status: string;
  date: string | Date;
  id: string;
  checkinTime: string;
  checkoutTime: string;
  hoursWorked: number;
  adminComment: {
    time: string;
    comment: string;
  }[];
  employeeName?: string;
};

interface Props {
  attendance: Attendance;
  employeeName?: string;
}

const ATTENDANCE_COLORS: {
  [key: string]: string;
} = {
  "Half-day": "#FFFF00",
  Present: "#00FF00",
  Absent: "#FF0000",
  "Paid Leave": "#00CED1",
  "On Leave": "#FFA500",
  "Not Counting": "#808080",
};

const ATTENDANCE_FOREGROUNDS: {
  [key: string]: string;
} = {
  "Half-day": "black",
  Present: "black",
  Absent: "black",
  "Paid Leave": "black",
  "On Leave": "black",
  "Not Counting": "black",
};

export const AttendanceEvent = ({ attendance, employeeName }: Props) => {
  const { empId } = useParams() as {
    empId: string;
  };

  const key = Cookies.get("key") as string;
  const [open, setOpen] = useState(false);

  const [isCommentOpen, setIsCommentOpen] = useState(false);
  const [commentValue, setCommentValue] = useState("");

  // State for attendance change confirmation modal
  const [isChangeModalOpen, setIsChangeModalOpen] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<string | null>(null);

  const queryClient = useQueryClient();

  const colorAtt = ATTENDANCE_FOREGROUNDS[attendance.status];
  const bgAtt = ATTENDANCE_COLORS[attendance.status];

  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState(attendance?.status);

  // Fetch current admin info
  const { data: adminData } = useQuery({
    queryKey: ["current-admin"],
    queryFn: () => getCurrentAdmin(key),
  });

  const { mutate: ChangeStatus, isPending: isChanging } = useMutation({
    mutationFn: changeAttendanceStatusByAdmin(key),
  });

  // Handler to open confirmation modal
  const handleStatusSelect = (newStatus: string) => {
    if (newStatus === attendance?.status) return;
    setPendingStatus(newStatus);
    setIsChangeModalOpen(true);
    setIsOpen(false); // Close dropdown
  };

  // Handler for confirmed status change
  const onStatusChangeConfirmed = async (reason: string) => {
    if (!attendance?.id || !pendingStatus) return;

    ChangeStatus(
      {
        id: attendance.id,
        status: pendingStatus,
        reason: reason,
        acknowledgedChange: true,
      },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries({
            queryKey: ["empoyee-attendace"],
          });

          setIsChangeModalOpen(false);
          setPendingStatus(null);
          toast.success("Attendance Updated!");
        },
        onError: (error) => {
          console.log("ERROR WHILE CHANGING ATTENDANCE STATUS", error);
          const isAxiosError = error instanceof AxiosError;
          if (isAxiosError) {
            const errorReason = error?.response?.data?.error as string;
            toast.error(errorReason);
          } else {
            toast.error(error?.message);
          }
        },
      }
    );
  };

  const { mutate: AddComment, isPending: isCommentAdding } = useMutation({
    mutationFn: addAttendanceCommmentByNewWay(key),
  });

  const onCommentAdd = async (comment: string) => {
    if (!comment) return;

    let date: Date;

    if (attendance?.date) {
      date = new Date(attendance?.date);

      date.setHours(0, 0, 0, 0);
      date = new Date(date);
    } else {
      date = new Date();

      date.setHours(0, 0, 0, 0);
      date = new Date(date);
    }

    AddComment(
      {
        employee: empId,
        adminComment: [comment],
        date,
      },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries({
            queryKey: ["empoyee-attendace"],
          });

          toast.success("Comment Added!");
          // setIsCommentOpen(true);
        },
        onError: (error) => {
          console.log("ERROR WHILE ADDING ATTENDANCE COMMENT", error);
          const isAxiosError = error instanceof AxiosError;
          if (isAxiosError) {
            const errorReason = error?.response?.data?.error as string;
            toast.error(errorReason);
          } else {
            toast.error(error?.message);
          }
        },
      }
    );
  };

  const commentDate = useMemo(() => {
    if (attendance?.date) {
      let date = new Date(attendance?.date);
      date.setHours(0, 0, 0, 0);
      date = new Date(date);

      // date = new Date(date.getTime() - 5.5 * 60 * 60 * 1000);

      return date;
    }

    let date = new Date();
    date.setHours(0, 0, 0, 0);
    date = new Date(date);

    // date = new Date(date.getTime() - 5.5 * 60 * 60 * 1000);

    return date;
  }, [attendance]);

  const { data: CommentsData, isPending: isCommentsLoading } = useQuery({
    queryKey: [
      "attendance-comments",
      {
        employee: empId,
        date: commentDate,
      },
    ],
    queryFn: () =>
      getAttendanceCommentsByDateAsAdmin(key)({
        employee: empId,
        date: commentDate,
      }),
    // enabled: isCommentOpen,
  });

  const commentsByDate = useMemo(() => {
    if (isCommentsLoading) {
      return [];
    }

    if (CommentsData === undefined || CommentsData?.comments === undefined) {
      return [];
    }

    const result = CommentsData?.comments?.map((com) => {
      return {
        id: com?._id,
        adminComment: com?.adminComment?.[0],
        date: com?.date,
      };
    });

    return result;
  }, [CommentsData, isCommentsLoading]);

  return (
    <div>
      <div
        style={{
          backgroundColor: bgAtt,
          color: colorAtt,
        }}
      >
        <div className={cn("flex items-center justify-between px-2", colorAtt)}>
          <AlertDialog open={open} onOpenChange={setOpen}>
            <AlertDialogTrigger className="tracking-normal">
              <div
                className={cn(
                  "flex items-center justify-between px-3 py-1",
                  colorAtt
                )}
              >
                <div>{attendance?.status}</div>
              </div>
            </AlertDialogTrigger>
            <AlertDialogContent className={cn("transition-all duration-300")}>
              <AlertDialogCancel className="absolute right-2 top-2 h-7 w-7 p-0 border-none">
                <IoClose className="w-5 h-5" />
              </AlertDialogCancel>
              <AlertDialogHeader>
                <AlertDialogTitle>Attendance</AlertDialogTitle>
              </AlertDialogHeader>

              <div className="flex">
                <div className="flex flex-col p-3 shrink-0">
                  <h5>
                    <span>Attendance Marking : </span>
                    <span className="font-semibold">{attendance?.status}</span>
                  </h5>
                  <h5 className="mt-1">
                    <span>Hours worked : </span>
                    <span className="font-semibold">
                      {attendance?.hoursWorked}
                    </span>
                  </h5>
                  <h5 className="mt-1">
                    <span>Checkin Time : </span>
                    <span className="font-semibold">
                      {attendance?.checkinTime}
                    </span>
                  </h5>
                  <h5 className="mt-1">
                    <span>Checkout Time : </span>
                    <span className="font-semibold">
                      {attendance?.checkoutTime}
                    </span>
                  </h5>
                </div>
              </div>
            </AlertDialogContent>
          </AlertDialog>

          {attendance?.id && (
            <div>
              <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
                <DropdownMenuTrigger asChild>
                  <button>
                    <ChevronDown className="w-3 h-3" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56">
                  <DropdownMenuLabel>Attendance Status</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuRadioGroup
                    value={position}
                    onValueChange={setPosition}
                  >
                    <DropdownMenuRadioItem
                      value="Present"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        handleStatusSelect("Present");
                      }}
                      // Restriction: Cannot change Half-day to Present
                      disabled={isChanging || attendance?.status === "Half-day"}
                      title={attendance?.status === "Half-day" ? "Cannot upgrade Half-day to Present" : undefined}
                    >
                      Present
                    </DropdownMenuRadioItem>
                    <DropdownMenuRadioItem
                      value="Absent"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        handleStatusSelect("Absent");
                      }}
                      disabled={isChanging}
                    >
                      Absent
                    </DropdownMenuRadioItem>
                    <DropdownMenuRadioItem
                      value="Half-day"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        handleStatusSelect("Half-day");
                      }}
                      disabled={isChanging}
                    >
                      Half Day
                    </DropdownMenuRadioItem>
                    <DropdownMenuRadioItem
                      value="Paid Leave"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        handleStatusSelect("Paid Leave");
                      }}
                      disabled={isChanging}
                    >
                      Paid Leave
                    </DropdownMenuRadioItem>
                    <DropdownMenuRadioItem
                      value="On Leave"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        handleStatusSelect("On Leave");
                      }}
                      disabled={isChanging}
                    >
                      Unpaid Leave
                    </DropdownMenuRadioItem>
                    <DropdownMenuRadioItem
                      value="Holiday"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        handleStatusSelect("Holiday");
                      }}
                      disabled={isChanging}
                    >
                      Holiday
                    </DropdownMenuRadioItem>
                  </DropdownMenuRadioGroup>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}

          <div>
            <AlertDialog open={isCommentOpen} onOpenChange={setIsCommentOpen}>
              <AlertDialogTrigger className="tracking-normal">
                <div className="flex items-center">
                  <span>{commentsByDate?.length}</span>
                  <BiComment className="w-3 h-3" />
                </div>
              </AlertDialogTrigger>
              <AlertDialogContent
                className={cn("transition-all duration-300 max-w-6xl")}
              >
                <AlertDialogCancel className="absolute right-2 top-2 h-7 w-7 p-0 border-none">
                  <IoClose className="w-5 h-5" />
                </AlertDialogCancel>
                <AlertDialogHeader>
                  <AlertDialogTitle>Attendance Comments</AlertDialogTitle>
                </AlertDialogHeader>

                <div className="flex flex-col gap-2">
                  <div className="flex flex-row items-center gap-5">
                    <Input
                      placeholder="comment..."
                      value={commentValue}
                      onChange={(e) => {
                        setCommentValue(e.target.value);
                      }}
                    />
                    <Button
                      type="button"
                      onClick={() => onCommentAdd(commentValue)}
                      disabled={isCommentAdding}
                    >
                      Add Comment
                    </Button>
                  </div>

                  {commentsByDate?.length > 0 ? (
                    <div className="mt-4 max-h-[500px] overflow-y-auto">
                      <div className="flex flex-col gap-1">
                        {commentsByDate?.map((comment, index) => {
                          return (
                            <div
                              className="flex items-center justify-between bg-background text-foreground px-5 py-2 rounded-lg border"
                              key={index}
                            >
                              <p>{comment?.adminComment}</p>
                              <p className="text-[13px]">
                                {format(new Date(comment?.date), "PPP")}
                              </p>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <div className="mt-4 flex items-center justify-center">
                      <p>No comments yet.</p>
                    </div>
                  )}
                </div>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </div>

      <div className="bg-white flex flex-col text-black px-1 max-w-full flex-wrap">
        <p className="text-[11px]">In - {attendance?.checkinTime}</p>
        <p className="text-[11px]">Out - {attendance?.checkoutTime}</p>
        {/* <p className="text-[11px]">Hours - {attendance?.hoursWorked}</p> */}
      </div>

      {/* Attendance Change Confirmation Modal */}
      <AttendanceChangeModal
        isOpen={isChangeModalOpen}
        onClose={() => {
          setIsChangeModalOpen(false);
          setPendingStatus(null);
        }}
        onConfirm={onStatusChangeConfirmed}
        isLoading={isChanging}
        adminName={adminData?.foundAdmin?.name || "Admin"}
        employeeName={employeeName || attendance?.employeeName || "Employee"}
        currentStatus={attendance?.status}
        newStatus={pendingStatus || ""}
        attendanceDate={attendance?.date}
      />
    </div>
  );
};
